import { createApi } from '@reduxjs/toolkit/query/react';
import { createCustomFetchBaseQuery } from '../baseQuery';

export const categoriesApi = createApi({
  reducerPath: 'categories',
  baseQuery: createCustomFetchBaseQuery(),
  tagTypes: ['categories'],
  endpoints: (builder) => ({
    getAllCategories: builder.query({
      query: ({ page = 1, limit = 10, keyword = '', status, lang = '' } = {}) => {
        let queryString = `?page=${page}&limit=${limit}`;
        if (status) queryString += `&status=${status}`;
        if (keyword) {
          queryString += `&keyword=${keyword}`;
        }
        if (lang) queryString += `&lang=${lang}`;
        return {
          url: `categories/getall${queryString}`,
          method: 'GET',
        };
      },
      transformResponse: (res) => ({
        data: res.data,
        meta: res.meta,
      }),
      providesTags: ['categories'],
    }),
    getAllCategoriesSelected: builder.query({
      query: ({ page = 1, limit = 10, keyword = '', status, lang = '' } = {}) => {
        let queryString = `?page=${page}&limit=${limit}`;
        if (status) queryString += `&status=${status}`;
        if (keyword) {
          queryString += `&keyword=${keyword}`;
        }
        if (lang) queryString += `&lang=${lang}`;
        return {
          url: `categories/${queryString}`,
          method: 'GET',
        };
      },
      transformResponse: (res) => ({
        data: res.data,
        meta: res.meta,
      }),
      providesTags: ['categories'],
    }),
    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['categories'],
    }),
    updateCategory: builder.mutation({
      query: ({ id, body }) => ({
        url: `categories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['categories'],
    }),
    addCategory: builder.mutation({
      query: (body) => ({
        url: `categories`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['categories'],
    }),
    getAllSubCategories: builder.query({
      query: ({ categoryId, page = 1, limit = 10, keyword = '', status } = {}) => {
        let queryString = `?page=${page}&limit=${limit}`;
        if (status) queryString += `&status=${status}`;
        if (keyword) queryString += `&keyword=${keyword}`;
        return {
          url: `subcategories/${categoryId}/getall${queryString}`,
          method: 'GET',
        };
      },
      transformResponse: (res) => ({
        data: res.data,
        meta: res.meta,
      }),
      providesTags: ['subcategories'],
    }),
    updateSubcategory: builder.mutation({
      query: ({ id, body }) => ({
        url: `subcategories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['subcategories'],
    }),
    deleteSubcategory: builder.mutation({
      query: (id) => ({
        url: `subcategories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['subcategories'],
    }),
    addSubcategory: builder.mutation({
      query: ({ categoryId, body }) => ({
        url: `subcategories/${categoryId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['subcategories'],
    }),
    getAllLevelQuestions: builder.query({
      query: ({ page = 1, limit = 10, status = 'active', keyword = '', gameName,lang,category,subCategory }) => ({
        url: '/level-questions/levels/admin',
        method: 'GET',
        params: { page, limit, status, keyword, gameName,lang,category,subCategory },
      }),
      transformResponse: (response) => ({
        data: response.data,
        meta: response.meta,
      }),
      providesTags: ['levels'],
    }),
    addLevelQuestion: builder.mutation({
      query: ({ body, gameLevel }) => ({
        url: `level-questions/levels/${gameLevel}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['levels'],
    }),
    updateLevelQuestion: builder.mutation({
      query: ({ id, body }) => ({
        url: `level-questions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['levels'],
    }),
    deleteLevelQuestion: builder.mutation({
      query: (id) => ({
        url: `level-questions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['levels'],
    }),
    getAllLevels: builder.query({
      query: ({ page = 1, limit = 10, status = 'active', keyword = '', gameName, lang = '' }) => ({
        url: '/game-levels/all',
        method: 'GET',
        params: { page, limit, status, keyword, gameName, lang },
      }),
      transformResponse: (response) => response.data,
      providesTags: ['levels'],
    }),
    getAllLevelsAdmin: builder.query({
      query: ({ page = 1, limit = 10, status = '', keyword = '' }) => ({
        url: '/game-levels/getall',
        method: 'GET',
        params: { page, limit, status, keyword },
      }),
      transformResponse: (response) => response,
      providesTags: ['levels'],
    }),
    addLevels: builder.mutation({
      query: ({ body, gameName }) => ({
        url: 'game-levels/',
        params: { gameName },
        method: 'POST',
        body,
      }),
      invalidatesTags: ['levels'],
    }),
    updateLevels: builder.mutation({
      query: ({ id, body }) => ({
        url: `game-levels/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['levels'],
    }),
    deleteLevels: builder.mutation({
      query: (id) => ({
        url: `game-levels/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['levels'],
    }),
  }),
});

export const {
  useGetAllCategoriesQuery,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
  useAddCategoryMutation,
  useGetAllSubCategoriesQuery,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
  useAddSubcategoryMutation,
  useGetAllLevelQuestionsQuery,
  useAddLevelQuestionMutation,
  useUpdateLevelQuestionMutation,
  useDeleteLevelQuestionMutation,
  useGetAllLevelsQuery,
  useGetAllCategoriesSelectedQuery,
  useGetAllLevelsAdminQuery,
  useAddLevelsMutation,
  useUpdateLevelsMutation,
  useDeleteLevelsMutation,
} = categoriesApi;
